/**
 * Availability lookup, shared by the booking form (a new booking) and the customer portal
 * (rescheduling an existing one). The vendor's `mode` discriminates the two.
 *
 * Deliberately owns neither abort controllers nor dispatch — each controller already has its own
 * in-flight bookkeeping, and this stays a plain call returning normalised days.
 */
import type { ApiClient } from './client';
import { slotsFromSpots, type DaySlots } from '../domain/dates';

export interface SpotsQuery {
  /** First date of the window, YYYY-MM-DD in the tenant's timezone. */
  date: string;
  /** Window length; the vendor caps this at 45. */
  days: number;
  mode: 'new' | 'reschedule';
  locationId?: number | null;
  /**
   * Minutes the job needs. The vendor filters out slots too short for it — worth sending when
   * rescheduling, since the existing booking's duration is known.
   */
  duration?: number | null;
}

export async function fetchSpots(
  client: Pick<ApiClient, 'getSpots'>,
  query: SpotsQuery,
  signal?: AbortSignal,
): Promise<DaySlots[]> {
  const res = await client.getSpots(
    {
      date: query.date,
      days: query.days,
      mode: query.mode,
      grid: true,
      ...(query.locationId != null ? { location_id: query.locationId } : {}),
      ...(query.duration != null ? { duration: query.duration } : {}),
    },
    signal,
  );
  return slotsFromSpots(res);
}
